import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { PictureOutlined } from '@ant-design/icons';
import { Row, notification, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';

import BreadcrumbContainer from '../common/breadcrumbContainer/breadcrumbContainer';
import Permissions from '../auth/Permissions';
import OntraccrFileDragger from '../common/files/OntraccrFileUpload';
import StepsContainer from '../common/containers/StepsContainer';
import DrawerFooter from '../common/containers/DrawerFooter';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import ABWorkImportParsing from './ABWorkImportParsing';
import { prepareABWorkImportPayloads } from './ABWorkImportHelpers';
import { getIdMap } from '../helpers/helpers';
import {
  getABWorkReportingMapping,
  importABWorkReporting,
  acceptRejectWorksheets,
  findRevisionDifference,
  getABWorkReportingJobCodes,
} from './state/abWorkReporting.actions';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import { getStatuses } from '../statuses/state/statuses.actions';
import { getBoards } from '../boards/state/boards.actions';

const DEFAULT_CRUMBS = [{
  text: 'AB Work Import',
  icon: 'import',
}];

const PARSING_STEP = 1;

let didGetMapping = false;

export default function ABWorkImport({ history }) {
  const dispatch = useDispatch();
  const boards = useSelector((state) => state.boards.boards);
  const bucketTemplates = useSelector((state) => state.buckets.bucketTemplates);
  const cardTemplates = useSelector((state) => state.boards.cardTemplates);
  const abWorkReportingMapping = useSelector((state) => state.abWorkReporting.mapping);
  const statuses = useSelector((state) => state.statuses.statuses);

  const [currentStep, setCurrentStep] = useState(0);
  const [uploadFile, setUploadFile] = useState(null);
  const [importRows, setImportRows] = useState({});
  const [recordMap, setRecordMap] = useState({});
  const [parentMap, setParentMap] = useState({});
  const [childMap, setChildMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [isAcceptRejectFile, setIsAcceptRejectFile] = useState(false);
  const [hasParsingError, setHasParsingError] = useState(false);
  const [jobCode, setJobCode] = useState(null);
  const hasPermission = Permissions.has('AB_WORK_REPORTING');
  const canEditImportMappings = Permissions.has('EDIT_AB_WORK_REPORTING_SETTINGS');

  const bucketTemplateMap = useMemo(() => getIdMap(bucketTemplates), [bucketTemplates]);

  useEffect(() => {
    dispatch(getStatuses());
    dispatch(getABWorkReportingMapping());
    dispatch(getABWorkReportingJobCodes());
    dispatch(getBoards());
  }, []);

  const bucketMappings = useMemo(() => {
    try {
      const raw = abWorkReportingMapping?.bucketMappings;
      return (typeof raw === 'string' ? JSON.parse(raw) : raw) || {};
    } catch (_err) {
      return {};
    }
  }, [abWorkReportingMapping]);

  const fieldMappings = useMemo(() => {
    try {
      const raw = abWorkReportingMapping?.fieldMappings;
      return (typeof raw === 'string' ? JSON.parse(raw) : raw) || {};
    } catch (_err) {
      return {};
    }
  }, [abWorkReportingMapping]);

  const selectedBoard = abWorkReportingMapping?.boardId || null;
  const selectedStatus = abWorkReportingMapping?.statusId || null;
  const selectedRevisionTextField = abWorkReportingMapping?.revisionTextField || null;

  if (!didGetMapping) {
    dispatch(getABWorkReportingMapping());
    didGetMapping = true;
  }

  const onUpload = ({ file, onSuccess, onError }) => {
    if (file.type !== 'text/plain') {
      setImmediate(() => {
        onError();
        notification.error({
          message: 'Error',
          description: 'Only text files are supported.',
        });
      });
      return;
    }
    setImmediate(() => onSuccess());
    setUploadFile(file);
  };

  const onRemove = (file) => {
    if (file?.uid === uploadFile?.uid) {
      setUploadFile(null);
    }
  };

  const fileList = uploadFile && uploadFile instanceof File ? [{
    ...uploadFile,
    url: URL.createObjectURL(uploadFile),
    name: uploadFile.name,
  }] : [];

  const steps = [
    {
      title: 'Upload File',
      content: (
        <OntraccrFileDragger
          customRequest={onUpload}
          onRemove={onRemove}
          Icon={PictureOutlined}
          maxCount={1}
          fileList={fileList}
        />
      ),
    },
    {
      title: 'Parse File',
      content: (
        <ABWorkImportParsing
          uploadFile={uploadFile}
          importRows={importRows}
          setImportRows={setImportRows}
          setRecordMap={setRecordMap}
          setParentMap={setParentMap}
          setChildMap={setChildMap}
          isAcceptRejectFile={isAcceptRejectFile}
          setIsAcceptRejectFile={setIsAcceptRejectFile}
          hasParsingError={hasParsingError}
          setHasParsingError={setHasParsingError}
          jobCode={jobCode}
          setJobCode={setJobCode}
        />
      ),
    },
  ];

  const onNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prevStep) => prevStep + 1);
    } else if (!isAcceptRejectFile) {
      const board = boards[selectedBoard];
      if (!board) {
        notification.error({
          message: 'Error',
          description: 'No board configured. Please set up import mappings in AB Work Reporting settings.',
        });
        return;
      }

      setLoading(true);

      let revisionResult;

      if (!isAcceptRejectFile) {
        const record15Data = recordMap[importRows['15']?.[0]] || null;
        const workOrderNumber = record15Data?.workOrderNumber?.trim() || '';
        const incomingRevisionNumber = parseInt(record15Data?.revisionNumber, 10) || 0;

        const bidItemParentMap = {};
        const segmentParentMap = {};
        const items = [];
        const segments = [];

        (importRows['20'] || []).forEach((id) => {
          const record = recordMap[id];
          if (!record) return;
          items.push(record);
          parentMap[id]?.forEach(({ originalId: childId } = {}) => {
            segmentParentMap[childId] = record.workOrderItemNumber;
          });
        });
        (importRows['22'] || []).forEach((id) => {
          const record = recordMap[id];
          if (!record) return;
          const relevantItemName = segmentParentMap[record.originalId || record.id];
          segments.push({
            ...record,
            itemName: relevantItemName,
          });
          parentMap[id]?.forEach(({ originalId: childId } = {}) => {
            if (!bidItemParentMap[childId]) {
              bidItemParentMap[childId] = {
                segmentNames: [],
                itemName: relevantItemName,
              };
            }
            bidItemParentMap[childId].segmentNames.push(record.roadSegmentIdentifier);
          });
        });
        const seenOriginalIds = new Set();
        const bidItems = (importRows['25'] || []).reduce((acc, id) => {
          const record = recordMap[id];
          if (!record) return acc;
          const origId = record.originalId || record.id;
          if (seenOriginalIds.has(origId)) return acc;
          seenOriginalIds.add(origId);
          acc.push({
            ...record,
            ...(bidItemParentMap[origId] || {}),
          });
          return acc;
        }, []);

        revisionResult = await dispatch(findRevisionDifference({
          workOrderNumber,
          incomingRevisionNumber,
          record15Data,
          jobCode,
          items,
          segments,
          bidItems,
        }));

        if (revisionResult?.status === 'reject_outdated' || revisionResult?.status === 'reject_duplicate') {
          setLoading(false);
          await CustomConfirmModal({
            title: revisionResult.errorTitle,
            content: <div>{revisionResult.errorMessage}</div>,
            hideCancel: true,
          });
          return;
        }
      }

      const { bucketInserts, cardPayload } = prepareABWorkImportPayloads({
        bucketMappings,
        fieldMappings,
        importRows,
        parentMap,
        childMap,
        selectedBoard: board,
        selectedStatus,
        bucketTemplateMap,
        recordMap,
        cardTemplates,
        statuses,
        jobCode,
      });

      const revisionDiff = (revisionResult && revisionResult.status !== 'new')
        ? revisionResult
        : null;

      if (bucketInserts.length || cardPayload) {
        const result = await dispatch(importABWorkReporting({
          bucketMappings,
          fieldMappings,
          selectedBoard,
          selectedStatus,
          bucketInserts,
          cardPayload,
          revisionTextField: selectedRevisionTextField,
          revisionDiff,
        }));

        if (result) {
          history.push('/boards', { boardId: result.boardId, cardId: result.id, activeTab: 'details' });
          didGetMapping = false;
        }
      }

      setLoading(false);
    } else {
      const worksheets = importRows['62']?.map((row) => recordMap[row])?.filter(Boolean) || [];
      if (!worksheets.length) return;

      setLoading(true);
      const result = await dispatch(acceptRejectWorksheets({
        worksheets,
        batchNumber: parseInt(worksheets[0]?.batchTransferSequence, 10),
      }));

      setLoading(false);

      if (result) {
        const resetState = () => {
          setCurrentStep(0);
          setUploadFile(null);
          setImportRows({});
          setRecordMap({});
          setParentMap({});
          setChildMap({});
          setIsAcceptRejectFile(false);
        };

        const {
          acceptedWorksheets,
          rejectedWorksheets,
          pendingWorksheets,
        } = result;

        await CustomConfirmModal({
          title: 'Accept/Reject file processed successfully',
          content: (
            <div>
              <div><b>Accepted Worksheets:</b> {acceptedWorksheets.length}</div>
              <div><b>Rejected Worksheets:</b> {rejectedWorksheets.length}</div>
              <div><b>Pending Worksheets:</b> {pendingWorksheets.length}</div>
            </div>
          ),
          onOk: resetState,
          hideCancel: true,
        });
      }
    }
  };

  const getStepValidation = () => {
    if (currentStep === 0) {
      return uploadFile
        ? { valid: true, disabledReason: null }
        : { valid: false, disabledReason: 'Upload a text file (.txt) to continue.' };
    }

    if (
      currentStep === PARSING_STEP && (
        hasParsingError
        || (!isAcceptRejectFile && !jobCode)
      )
    ) {
      if (hasParsingError) {
        return { valid: false, disabledReason: 'Resolve file parsing errors before continuing.' };
      }
      return { valid: false, disabledReason: 'Enter a job code to continue.' };
    }

    if (isAcceptRejectFile && currentStep === steps.length - 1) {
      return importRows['62']?.length
        ? { valid: true, disabledReason: null }
        : {
          valid: false,
          disabledReason: 'No accept/reject worksheet rows found. Check the file format.',
        };
    }

    if (!isAcceptRejectFile && currentStep === steps.length - 1) {
      const missing = [];
      if (!selectedBoard) missing.push('target board');
      if (!selectedStatus) missing.push('default card status');
      if (!fieldMappings.cardTitle) missing.push('card title mapping');
      if (missing.length) {
        const list = missing.length === 1
          ? missing[0]
          : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`;
        return {
          valid: false,
          disabledReason: canEditImportMappings
            ? `Set ${list} under Settings → AB Work Reporting → Import mappings.`
            : `Import setup is incomplete (${list}). Contact a company administrator to configure AB Work Reporting import mappings.`,
        };
      }
      return { valid: true, disabledReason: null };
    }

    return { valid: true, disabledReason: null };
  };

  const { valid: stepValid, disabledReason } = getStepValidation();

  if (!hasPermission) {
    history.push('/dashboard');
    return null;
  }

  return (
    <BreadcrumbContainer crumbs={DEFAULT_CRUMBS} redesignCrumbStyle={{ marginLeft: 5 }}>
      <StepsContainer
        containerStyle={{
          height: '90%',
          backgroundColor: 'white',
          padding: 24,
          borderRadius: 12,
          border: '1px solid #E5E7EB',
          overflow: 'auto',
        }}
        currentStep={currentStep}
        steps={steps}
      />
      <DrawerFooter>
        <Row justify="end" align="middle" gutter={8}>
          {currentStep > 0 && (
            <OnTraccrButton
              title="Back"
              type="back"
              id="customer-add-project-footer-back"
              onClick={() => setCurrentStep((prevStep) => prevStep - 1)}
            />
          )}
          <Tooltip title={disabledReason || undefined} placement="topRight">
            <span
              style={{
                display: 'inline-block',
                cursor: stepValid ? undefined : 'not-allowed',
              }}
            >
              <OnTraccrButton
                title={currentStep < steps.length - 1 ? 'Next' : 'Submit'}
                onClick={onNext}
                disabled={!stepValid}
                loading={loading}
              />
            </span>
          </Tooltip>
        </Row>
      </DrawerFooter>
    </BreadcrumbContainer>
  );
}

ABWorkImport.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};
